import {
    areSearchParamsEqual,
    IVR_CONFIGURATION_ENCODED_QUERY_KEY,
    getHashSearchParams,
    unwrapEncodedSearchParams,
    wrapEncodedSearchParams,
} from './encodedUrlQuery';

export { areSearchParamsEqual };

export type IvrConfigurationFilterItem = { key: string; value: any };

export type IvrConfigurationUrlViewState = {
    hasAny: boolean;
    page: number;
    size: number;
    orderBy: string | null;
    isDesc: boolean;
    sortOrder: number;
    filterFlowId: IvrConfigurationFilterItem | null;
    filterName: IvrConfigurationFilterItem | null;
    filterIsActive: IvrConfigurationFilterItem[] | null;
    uiFilters: Record<string, any>;
};

export type IvrConfigurationUrlWriteState = {
    page: number;
    size: number;
    orderBy: string | null;
    isDesc: boolean;
    sortOrder: number;
    filterFlowId: IvrConfigurationFilterItem | null;
    filterName: IvrConfigurationFilterItem | null;
    filterIsActive: IvrConfigurationFilterItem[] | null;
};

const DEFAULT_PAGE = 1;
const DEFAULT_SIZE = 10;

export function readInitialIvrConfigurationUrlState(): IvrConfigurationUrlViewState {
    return parseIvrConfigurationUrlState(getHashSearchParams());
}

export function parseIvrConfigurationUrlState(params: URLSearchParams): IvrConfigurationUrlViewState {
    params = unwrapEncodedSearchParams(params, IVR_CONFIGURATION_ENCODED_QUERY_KEY);
    const uiFilters: Record<string, any> = {};
    const page = parsePositiveInt(params.get('page'), DEFAULT_PAGE);
    const size = parsePositiveInt(params.get('size'), DEFAULT_SIZE);
    const orderBy = params.get('orderBy') || null;
    const isDesc = parseBooleanParam(params.get('desc'), true);
    const sortOrder = parseSortOrder(params.get('sortOrder'), orderBy, isDesc);

    const flowId = params.get('flowId');
    const name = params.get('name');
    const isActive = getMultiParams(params, 'isActive').map(toBooleanValue).filter((value) => value !== null);

    if (flowId) uiFilters.flowId = flowId;
    if (name) uiFilters.name = name;
    if (isActive.length) uiFilters.isActive = isActive;

    return {
        hasAny: params.toString().length > 0,
        page,
        size,
        orderBy,
        isDesc,
        sortOrder,
        filterFlowId: toFilterItem('FlowId', flowId),
        filterName: toFilterItem('Name', name),
        filterIsActive: toFilterList('IsActive', isActive.map((value) => String(value))),
        uiFilters,
    };
}

export function buildIvrConfigurationSearchParams(state: IvrConfigurationUrlWriteState): URLSearchParams {
    return wrapEncodedSearchParams(buildPlainIvrConfigurationSearchParams(state), IVR_CONFIGURATION_ENCODED_QUERY_KEY);
}

function buildPlainIvrConfigurationSearchParams(state: IvrConfigurationUrlWriteState): URLSearchParams {
    const params = new URLSearchParams();

    if (state.page && state.page !== DEFAULT_PAGE) {
        params.set('page', String(state.page));
    }
    if (state.size && state.size !== DEFAULT_SIZE) {
        params.set('size', String(state.size));
    }
    if (state.orderBy && !(state.orderBy === 'FlowId' && state.sortOrder === 0)) {
        params.set('orderBy', state.orderBy);
        params.set('desc', state.isDesc ? '1' : '0');
        if (state.sortOrder) {
            params.set('sortOrder', String(state.sortOrder));
        }
    }

    setScalar(params, 'flowId', state.filterFlowId?.value);
    setScalar(params, 'name', state.filterName?.value);
    setMulti(params, 'isActive', state.filterIsActive);

    return params;
}

function setScalar(params: URLSearchParams, key: string, value?: string | null) {
    if (value) {
        params.set(key, value);
    }
}

function setMulti(params: URLSearchParams, key: string, items?: IvrConfigurationFilterItem[] | null) {
    items?.forEach((item) => {
        if (item?.value === undefined || item?.value === null || item.value === '') return;
        params.append(key, normalizeBooleanParam(item.value));
    });
}

function getMultiParams(params: URLSearchParams, key: string): string[] {
    const values: string[] = [];
    params.getAll(key).forEach((value) => {
        value.split(',').forEach((part) => {
            const trimmed = part.trim();
            if (trimmed) values.push(trimmed);
        });
    });
    return values;
}

function toFilterItem(key: string, value?: string | null): IvrConfigurationFilterItem | null {
    if (!value) return null;
    return { key, value };
}

function toFilterList(key: string, values?: Array<any>): IvrConfigurationFilterItem[] | null {
    if (!values?.length) return null;
    return values.map((value) => ({ key, value }));
}

function toBooleanValue(value: string): boolean | null {
    if (value === '1' || value === 'true') return true;
    if (value === '0' || value === 'false') return false;
    return null;
}

function normalizeBooleanParam(value: any): string {
    if (value === true || value === 'true' || value === '1') return '1';
    if (value === false || value === 'false' || value === '0') return '0';
    return String(value);
}

function parsePositiveInt(value: string | null, fallback: number): number {
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < 1) {
        return fallback;
    }
    return parsed;
}

function parseBooleanParam(value: string | null, fallback: boolean): boolean {
    if (value == null) return fallback;
    return value === '1' || value === 'true';
}

function parseSortOrder(value: string | null, orderBy: string | null, isDesc: boolean): number {
    if (value === '1' || value === '-1' || value === '0') {
        return Number(value);
    }
    if (!orderBy) return 0;
    return isDesc ? -1 : 1;
}
